import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight, X, Play } from "lucide-react";

// ─────────────────────────────────────────────────────────────────
// Replace videoId values with your actual One Light Stays YouTube IDs
// ─────────────────────────────────────────────────────────────────
const VIDEOS = [
  {
    id: 1,
    title: "Rudram Aura — A Himalayan Escape",
    location: "Rishikesh, Uttarakhand",
    videoId: "A-Zcjg1_y5U",
  },
  {
    id: 2,
    title: "The Long Stay Goa — Beachside Bliss",
    location: "South Goa",
    videoId: "WxXFaxQdSQg",
  },
  {
    id: 3,
    title: "Weekend GetAway Camps — Into the Wild",
    location: "Karjat, Maharashtra",
    videoId: "wTSqtXwKDno",
  },
  {
    id: 4,
    title: "Mermaid Villa — Coastal Serenity",
    location: "Alibaug, Maharashtra",
    videoId: "VfbxSV-O9NY",
  },
  {
    id: 5,
    title: "Great Hill View — Above the Clouds",
    location: "Mahabaleshwar, Maharashtra",
    videoId: "wjHVwO89upg",
  },
];

function getThumb(videoId) {
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

export default function VideoCarousel() {
  const [index, setIndex] = useState(0);
  const [activeVideo, setActiveVideo] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const overlayRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(3);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setVisibleCount(1);
      } else if (window.innerWidth < 1024) {
        setVisibleCount(2);
      } else {
        setVisibleCount(3);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const maxIndex = Math.max(0, VIDEOS.length - visibleCount);

  useEffect(() => {
    setIndex((prev) => Math.min(prev, maxIndex));
  }, [maxIndex]);

  const navigate = useCallback(
    (dir) => {
      if (isAnimating) return;
      setIsAnimating(true);
      setIndex((i) => Math.max(0, Math.min(i + dir, maxIndex)));
      setTimeout(() => setIsAnimating(false), 400);
    },
    [isAnimating, maxIndex]
  );

  // Close modal on Escape key
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveVideo(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = activeVideo ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [activeVideo]);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) setActiveVideo(null);
  };

  return (
    <>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 my-16">
        {/* Header */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="ols-label mb-1">Watch &amp; Explore</div>
            <h2 className="font-display text-3xl md:text-4xl">
              Experience Our Stays
            </h2>
          </div>
          {/* Arrow Controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              disabled={index === 0}
              aria-label="Previous videos"
              className="w-9 h-9 rounded-full border border-stone-300 flex items-center justify-center text-stone-700 hover:bg-stone-900 hover:text-white hover:border-stone-900 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate(1)}
              disabled={index >= maxIndex}
              aria-label="Next videos"
              className="w-9 h-9 rounded-full border border-stone-300 flex items-center justify-center text-stone-700 hover:bg-stone-900 hover:text-white hover:border-stone-900 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Carousel Track — desktop: 3 cards; mobile: 1 card */}
        <div className="overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
            style={{
              gap: "20px",
              transform: `translateX(calc(-${index} * (100% / ${visibleCount} + ${20 * (visibleCount - 1) / visibleCount}px)))`,
            }}
          >
            {VIDEOS.map((v) => (
              <VideoCard
                key={v.id}
                video={v}
                visibleCount={visibleCount}
                onClick={() => setActiveVideo(v)}
              />
            ))}
          </div>
        </div>

        {/* Dot indicators */}
        <div className="flex justify-center gap-1.5 mt-6">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-8 bg-stone-900" : "w-4 bg-stone-300"
                }`}
            />
          ))}
        </div>
      </section>

      {/* ── VIDEO MODAL ─────────────────────────────────────────── */}
      {activeVideo && (
        <div
          ref={overlayRef}
          onClick={handleOverlayClick}
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4 backdrop-blur-sm"
          style={{ animation: "ols-fade-up 0.25s cubic-bezier(0.22,1,0.36,1) both" }}
        >
          <div className="relative w-full max-w-4xl">
            {/* Close button */}
            <button
              onClick={() => setActiveVideo(null)}
              aria-label="Close video"
              className="absolute -top-10 right-0 w-9 h-9 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center text-white transition-colors duration-200 z-10"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Video title */}
            <div className="mb-3 px-1">
              <div className="text-white font-display text-lg md:text-xl">
                {activeVideo.title}
              </div>
              <div className="text-white/60 text-sm mt-0.5">{activeVideo.location}</div>
            </div>

            {/* Responsive iframe wrapper */}
            <div
              className="relative w-full rounded-2xl overflow-hidden shadow-2xl"
              style={{ paddingBottom: "56.25%" }}
            >
              <iframe
                key={activeVideo.videoId}
                src={`https://www.youtube.com/embed/${activeVideo.videoId}?autoplay=1&rel=0&modestbranding=1`}
                title={activeVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full border-0"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// VideoCard
// ─────────────────────────────────────────────────────────────────
function VideoCard({ video, visibleCount, onClick }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <button
      onClick={onClick}
      aria-label={`Play ${video.title}`}
      className="group flex-shrink-0 text-left focus:outline-none"
      style={{ width: `calc((100% - ${(visibleCount - 1) * 20}px) / ${visibleCount})` }}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video rounded-2xl overflow-hidden bg-stone-100 shadow-md group-hover:shadow-xl transition-shadow duration-300">
        <img
          src={getThumb(video.videoId)}
          alt={video.title}
          onLoad={() => setLoaded(true)}
          className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-105 ${loaded ? "opacity-100" : "opacity-0"
            }`}
        />
        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

        {/* Play button */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm border border-white/40 flex items-center justify-center group-hover:bg-white/35 group-hover:scale-110 transition-all duration-300 shadow-lg">
            <Play className="w-5 h-5 text-white fill-white ml-0.5" />
          </div>
        </div>

        {/* Location badge */}
        <div className="hidden absolute top-3 left-3">
          <span className="badge badge-dark text-[0.6rem] backdrop-blur-sm bg-black/60">
            {video.location}
          </span>
        </div>
      </div>

      {/* Card title */}
      <div className="hidden mt-3 px-1">
        <div className="font-semibold text-stone-900 text-sm line-clamp-2 group-hover:text-stone-600 transition-colors duration-200">
          {video.title}
        </div>
      </div>
    </button>
  );
}
